import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { MdClose, MdOutlineCheck } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import ReactSignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { consignmentService, scheduledCollectServices } from 'services';
import { useAuth, useScreenInfo } from 'hooks';
import {
  BatchCollectConsignment,
  CollectConsignmentWithDate,
  ConsignmentDetails,
} from 'types';
import {
  CollectConsignment,
  CollectReceiptModal,
} from 'pages/batch-collection/components';
import {
  Button,
  Heading,
  Input,
  Loading,
  Print,
  PrintTemplate,
  Select,
  Text,
} from 'components';
import { formValidations, getLocalStorageItem, htmlToImage } from 'helpers';
import {
  FontSize,
  FontWeight,
  LocalStorageKeys,
  TEST_IDS,
  deliveryModes,
  routes,
} from 'commons';
import * as S from './styles';

interface ConsignmentForCollection extends CollectConsignmentWithDate {
  createdDate?: Date;
}

export function ConfirmCollectOutlet() {
  const { selectedStore } = useAuth();
  const { isMobile } = useScreenInfo();
  const store = selectedStore.name || '';
  const firstRender = useRef(true);
  const navigate = useNavigate();

  const [signatureImg, setSignatureImg] = useState<string | undefined>(
    undefined
  );
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [isReceiptReady, setIsReceiptReady] = useState(false);
  const [consignmentsDetails, setConsignmentsDetails] = useState<
    ConsignmentDetails[]
  >([]);
  const batchCollectConsignments: BatchCollectConsignment[] =
    consignmentsDetails.map((consignmentsDetail) => {
      const { code, entries, payment } = consignmentsDetail;
      return {
        code,
        quantity: entries.length,
        subTotalPrice: payment.subTotalPrice,
      };
    });
  const [consignmentList, setConsignmentList] = useState<
    ConsignmentForCollection[]
  >(
    (() => {
      const storedConsignmentList = getLocalStorageItem(
        LocalStorageKeys.ConsignmentsToCollect,
        []
      );
      return storedConsignmentList;
    })()
  );
  const [isLoading, setIsLoading] = useState(true);

  const consignmentCodeList = consignmentList.map(
    (consignment: ConsignmentForCollection) => consignment.consignmentCode
  );

  const signRef = useRef<ReactSignatureCanvas>(null);
  const receiptTemplate = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (consignmentCodeList.length < 1) return;

    setIsLoading(true);

    const fetchConsignments = async () => {
      const promises = consignmentCodeList.map(async (consignment) => {
        return await consignmentService.getDetails(consignment);
      });

      const consignmentsData = await Promise.all(promises);

      const mergedConsignments: ConsignmentForCollection[] =
        consignmentsData.map((consignmentDetails) => {
          const { code, customer, createdDate } = consignmentDetails;
          const matchingConsignment = consignmentList.find(
            (consignment: ConsignmentForCollection) =>
              consignment.consignmentCode === code
          );

          let consignmentMerge = {
            consignmentCode: code,
            user: `${customer.firstName} ${customer.lastName}`,
            collectCode: '',
            createdDate: createdDate,
          };

          if (matchingConsignment) {
            consignmentMerge = {
              ...consignmentMerge,
              ...matchingConsignment,
            };
          }

          return consignmentMerge;
        });

      setConsignmentList(mergedConsignments);
      setConsignmentsDetails(consignmentsData);

      setIsLoading(false);
    };

    fetchConsignments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;

      return;
    }

    navigate(routes.batchCollection.path);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  const carrierValidation = Yup.object().shape({
    name: Yup.string().required('Nome'),
    cpf: Yup.string().required('CPF'),
    deliveryMode: Yup.string().required('Transportadora'),
  });

  const formCollect = useFormik({
    initialValues: {
      name: '',
      cpf: '',
      deliveryMode: '',
      consignments: consignmentCodeList,
    },

    validateOnBlur: false,

    validateOnChange: false,

    validationSchema: carrierValidation,

    validate: (values) => {
      formValidations.validationSchema({
        schema: carrierValidation,
        values,
      });
    },

    onSubmit: async (values) => {
      const { consignments, name, cpf, deliveryMode } = values;

      const codes = String(consignments);

      const digitalAssign = await receiptToImage();

      const response = await scheduledCollectServices.batchPost({
        store,
        codes,
        name,
        cpf,
        deliveryMode,
        digitalAssign,
      });

      if (response.status !== 200) {
        return toast.error(`Retirada falhou`);
      }

      toast.success(`Retirada de ${codes} realizada com sucesso`);

      setIsReceiptReady(true);
    },
  });

  const { handleSubmit, isSubmitting, handleChange, values, errors } =
    formCollect;

  const renderConsignmentList = consignmentList.map((consignment) => {
    const { consignmentCode } = consignment;

    return (
      <CollectConsignment
        isCheckboxHidden
        key={consignmentCode}
        name="consignments"
        onChange={handleChange}
        value={consignmentCode}
        consignment={consignment}
        disabled={isReceiptReady}
        defaultChecked={!isReceiptReady}
      />
    );
  });

  if (!consignmentList.length) {
    return (
      <S.Main>
        <Text size={FontSize.font12} weight={FontWeight.bold}>
          Nenhum pedido selecionado
        </Text>
      </S.Main>
    );
  }

  const selectedDeliveryMode = deliveryModes.find(
    (collector) => collector.value === values.deliveryMode
  );

  function clearSignCanvas() {
    if (!signRef || !signRef.current) return;

    signRef.current.clear();
    setSignatureImg(undefined);
  }

  async function receiptToImage() {
    if (!signRef.current || !receiptTemplate.current) return;

    const receiptImg = await htmlToImage(receiptTemplate.current);

    if (receiptImg) {
      return receiptImg;
    }
  }

  function saveSign() {
    if (!signRef || !signRef.current) return;

    setSignatureImg(signRef.current.getTrimmedCanvas().toDataURL('image/png'));
  }

  function submitSign() {
    handleSubmit();
  }

  function openSignModal() {
    setIsSignModalOpen(true);
  }

  function closeSignModal() {
    setIsSignModalOpen(false);
  }

  const renderFlow = () => {
    const { name, cpf } = values;
    const isSignatureUnavailable = !name || !cpf || !selectedDeliveryMode;

    if (isReceiptReady) {
      return (
        <Print
          data-testid={TEST_IDS.PRINT_COLLECTION_PROOF_BUTTON}
          Template={
            <PrintTemplate.CollectReceipt
              name={values.name}
              cpf={values.cpf}
              collector={selectedDeliveryMode?.label}
              batchCollectConsignments={batchCollectConsignments}
              signatureImage={signatureImg}
            />
          }
        >
          Imprimir comprovante de retirada
        </Print>
      );
    } else {
      return isMobile ? (
        <Button
          data-testid={TEST_IDS.CONFIRM_COLLECT_BUTTON}
          full
          type="button"
          disabled={isSubmitting || isSignatureUnavailable}
          onClick={openSignModal}
        >
          {isSubmitting ? 'Enviando...' : 'Assinatura do transportador'}
        </Button>
      ) : (
        <Button
          data-testid={TEST_IDS.CONFIRM_COLLECT_BUTTON}
          full
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Enviando...' : 'Registrar Retirada'}
        </Button>
      );
    }
  };

  return (
    <>
      <S.ReceiptModel ref={receiptTemplate}>
        <PrintTemplate.CollectReceipt
          name={values.name}
          cpf={values.cpf}
          collector={selectedDeliveryMode?.label}
          batchCollectConsignments={batchCollectConsignments}
          signatureImage={signatureImg}
        />
      </S.ReceiptModel>

      {isSignModalOpen && (
        <CollectReceiptModal
          name={values.name}
          cpf={values.cpf}
          batchCollectConsignments={batchCollectConsignments}
          collector={selectedDeliveryMode?.label}
          close={closeSignModal}
          isSubmitting={isSubmitting}
          isReceiptReady={isReceiptReady}
          signatureImage={signatureImg}
          signatureCanvas={
            <ReactSignatureCanvas
              ref={signRef}
              canvasProps={{ width: 300, height: 150, className: 'signCanvas' }}
              onEnd={saveSign}
            />
          }
          signatureButtons={
            <S.SignButtonsWrapper>
              <Button
                variant="danger"
                onClick={clearSignCanvas}
                disabled={isSubmitting}
              >
                <MdClose />
                Limpar
              </Button>
              <Button
                disabled={!signatureImg}
                variant="success"
                onClick={submitSign}
                isLoading={isSubmitting}
              >
                <MdOutlineCheck />
                Salvar
              </Button>
            </S.SignButtonsWrapper>
          }
        ></CollectReceiptModal>
      )}

      <S.Main onSubmit={handleSubmit}>
        <Text size={FontSize.font12} weight={FontWeight.bold}>
          Registre a retirada dos pedidos selecionados.
        </Text>
        <S.InputsSection>
          <S.InputWrapper>
            <S.Label htmlFor="name">Nome do Transportador</S.Label>

            <Input
              name="name"
              placeholder="Digite o nome completo"
              onChange={handleChange}
              value={values.name}
              status={errors.name ? 'error' : 'default'}
            />
          </S.InputWrapper>

          <S.InputWrapper>
            <S.Label htmlFor="cpf">CPF do Transportador</S.Label>

            <Input
              name="cpf"
              placeholder="xxx.xxx.xxx-xx"
              type="tel"
              onChange={handleChange}
              value={values.cpf}
              mask="999.999.999-99"
              status={errors.cpf ? 'error' : 'default'}
            />
          </S.InputWrapper>

          <S.InputWrapper>
            <S.Label htmlFor="deliveryMode">Transportadora</S.Label>

            <Select.Wrapper
              options={deliveryModes}
              name="deliveryMode"
              onChange={handleChange}
              status={errors.deliveryMode ? 'error' : 'default'}
            >
              <Select.Input placeholder="Selecione..." />

              <Select.StatusArrow />
            </Select.Wrapper>
          </S.InputWrapper>

          {renderFlow()}
        </S.InputsSection>
        <S.ConsignmentSection>
          {isLoading ? (
            <Loading />
          ) : (
            <S.ConsignmentWrapper>
              <Heading weight={FontWeight.bold}>Pedidos</Heading>

              <S.ConsignmentList>{renderConsignmentList}</S.ConsignmentList>
            </S.ConsignmentWrapper>
          )}
        </S.ConsignmentSection>
      </S.Main>
    </>
  );
}
